import { gql } from "apollo-boost";
import React from "react";
import styled from "styled-components/macro";
import { color, display, fontWeight } from "styled-system";
import Box from "../../components/Box";
import { Col, Row } from "../../components/Grid";
import { Heading } from "../../components/Headings";
import { AspectRatio, fragment as ImageFragment } from "../../components/Image";
import Link from "../../components/Link";
import Text from "../../components/Text";
import forwardProps, { removePropTypes } from "../../utils/forwardProps";
import ProductImage from "../../components/Image";

const Product = styled(Row)``;
export default Product;

export function Visual(props) {
  return <Col width={[1 / 4, 1 / 6]} minWidth={96} {...props} />;
}
Product.Visual = Visual;

function getFallbackImage({ wineType, wineColor, aspectX, aspectY }) {
  const type = `${wineType || "wine"}_${wineColor}`;
  const getCSS = (x, y) => {
    const width = (aspectY / aspectX / (y / x)) * 100;
    return `padding: 0 ${(100 - width) / 2}%; width: ${width}%;`;
  };
  const fallbacks = {
    default: {
      url:
        "https://cdn.bottlebooks.me/bottlebooks/img/bottlebooks-dashboard/icon_product-placeholder.svg",
      fileType: "svg",

      css: getCSS(9, 30)
    },
    wine_white: {
      publicId: "bottlebooks/bottlebooks/img/genericWhiteWineBottle-large",
      fileType: "jpg",
      transformation: [{ effect: "make_transparent:0" }, { effect: "trim" }]
    },
    wine_red: {
      publicId: "bottlebooks/bottlebooks/img/genericRedWineBottle-large.png",
      fileType: "png",
      transformation: [{ effect: "trim:0" }]
    }
  };
  return fallbacks[type] || fallbacks.default;
}

export function Image({
  image,
  aspectX = 1,
  aspectY = 2,
  wineType,
  wineColor,
  ...rest
}) {
  const hasImage = image && (image.publicId || image.url);
  // Find a fallback image:
  const fallbackImage =
    !hasImage && getFallbackImage({ wineType, wineColor, aspectX, aspectY });

  return (
    <Box {...rest}>
      <AspectRatio aspectRatio={aspectX / aspectY}>
        <ProductImage
          variant="bottle"
          aspectX={aspectX}
          aspectY={aspectY}
          {...fallbackImage}
          {...image}
        />
      </AspectRatio>
    </Box>
  );
}
Product.Image = Image;

export function Main(props) {
  return <Col {...props} />;
}
Product.Main = Main;

export function Stand({ stand }) {
  if (!stand || !stand.text) return null;
  return (
    <Text variant="stand" pb={3}>
      {stand.text}
    </Text>
  );
}
Product.Stand = Stand;

export function Producer({ producerName, to }) {
  if (!producerName || !producerName.name) return null;

  return (
    <Text variant="prefix" position="relative" mb={-3}>
      {to ? (
        <Link
          to={to}
          variant="default"
          mt={-3}
          pt={3}
          mb={-2}
          pb={2}
          display="inline-block"
        >
          {producerName.name}
        </Link>
      ) : (
        producerName.name
      )}
    </Text>
  );
}
Product.Producer = Producer;

export function Name({
  component: Component = Heading,
  product: { appellation, nameWithoutAppellation, vintage } = {},
  mt = 3,
  mb = 3,
  ...rest
}) {
  return (
    <Component mt={mt} mb={mb} {...rest}>
      {appellation !== nameWithoutAppellation && (
        <>
          <Appellation>{appellation}</Appellation>{" "}
        </>
      )}
      <ShortName>{nameWithoutAppellation}</ShortName>
      {vintage && (
        <>
          &nbsp;
          <Vintage>{vintage}</Vintage>
        </>
      )}
    </Component>
  );
}
Product.Name = Name;

export function Varieties({ text }) {
  return <Text variant="subline">{text}</Text>;
}
Product.Varieties = Varieties;

export const Appellation = styled(
  forwardProps(removePropTypes(display, fontWeight))("span")
)(display, fontWeight);
Appellation.defaultProps = {
  display: ["inline", "block"]
};
Appellation.propTypes = { ...display.propTypes };

export const ShortName = styled.span``;

export const Vintage = styled(
  forwardProps(removePropTypes(fontWeight, color))("span")
)(fontWeight, color);
Vintage.defaultProps = { fontWeight: "thin", color: "muted" };

export const fragments = {
  image: gql`
    fragment ProductImage on Product {
      bottleImage {
        ...Image
      }
      wineType
      wineColor
    }
    ${ImageFragment}
  `,
  stand: gql`
    fragment ProductStand on Product {
      stand {
        text
      }
    }
  `,
  producerName: gql`
    fragment ProductProducerName on Product {
      producer {
        producerName {
          name
        }
      }
    }
  `,
  name: gql`
    fragment ProductName on Product {
      appellation
      nameWithoutAppellation
      vintage
    }
  `,
  varieties: gql`
    fragment ProductVarieties on Product {
      varietiesText
    }
  `
};
